import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";

import { ActiveRallyVideo } from "./ActiveRallyVideo";
import { CondensedRallyTimeline_ContextOnly } from "./CondensedRallyTimeline_ContextOnly";
import { buildRallySegmentUrl } from "./GameStory_fastReactions";
import { FirefightClip } from "./GameStory_firefights";
import {
  PlayerIndexMap,
  RenderPlayerIdentity,
} from "./GameStoryDialog_UPDATED";
import { KitchenArrivalRow, ShotNumberOverlay } from "./RallyBoardPopup";
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { FloatingPanel } from "./GameStory_3rdShotPopup_Updated_Mar_18_2026_Ignore2";

export type ShotQualityEvent = {
  rallyIndex: number;
  shotIndex: number;
  shotNumber: number;

  team: 0 | 1;
  playerId: number;

  shotStartMs: number;
  shotEndMs: number;

  shotType?: string | null;
  normalizedShotType: string;

  strokeType?: string | null;
  isVolley?: boolean;
  isFinal?: boolean;

  qualityOverall: number | null;
  qualityExecution: number | null;
  qualitySelection: number | null;

  teamWonRally: boolean | null;
};

export type ShotQualityClip = {
  key: string;
  event: ShotQualityEvent;
  clipStartMs: number;
  clipEndMs: number;
};

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

function normalizeShotType(v: unknown): string {
  return typeof v === "string" ? v.trim().toLowerCase() : "";
}

function displayShotType(v: unknown): string {
  if (!(typeof v === "string" && v.trim())) return "—";
  const s = v.trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export function getOverlayShotQualityParts(overlay: ShotNumberOverlay): {
  overall: number | null;
  execution: number | null;
  selection: number | null;
} {
  const q = overlay?.rawShot?.quality as
    | { overall?: unknown; execution?: unknown; selection?: unknown }
    | undefined;

  return {
    overall: numOrNull(q?.overall),
    execution: numOrNull(q?.execution),
    selection: numOrNull((q as any)?.selection),
  };
}

export function formatOverlayShotQuality(
  overlay: ShotNumberOverlay,
): string | undefined {
  const { overall, execution, selection } = getOverlayShotQualityParts(overlay);
  const bits: string[] = [];

  if (overall != null) bits.push(`Q ${overall.toFixed(2)}`);
  // if (
  //   execution != null &&
  //   (overall == null || Math.abs(execution - overall) > 0.0001)
  // ) {
  //   bits.push(`Ex ${execution.toFixed(2)}`);
  // }
  // if (selection != null) bits.push(`Sel ${selection.toFixed(2)}`);

  return bits.length ? bits.join(" • ") : undefined;
}

export function formatShotQualityEvent(
  event: Pick<
    ShotQualityEvent,
    "qualityOverall" | "qualityExecution" | "qualitySelection"
  >,
): string {
  const bits: string[] = [];

  if (event.qualityOverall != null) {
    bits.push(`Q ${event.qualityOverall.toFixed(2)}`);
  }
  if (
    event.qualityExecution != null &&
    (event.qualityOverall == null ||
      Math.abs(event.qualityExecution - event.qualityOverall) > 0.0001)
  ) {
    bits.push(`Ex ${event.qualityExecution.toFixed(2)}`);
  }
  if (event.qualitySelection != null) {
    bits.push(`Sel ${event.qualitySelection.toFixed(2)}`);
  }

  return bits.join(" • ") || "No quality";
}

export function computeShotQualityEventsForRally(args: {
  rally: KitchenArrivalRow;
  rallyIndex: number;
  getTeamFromPlayerId: (playerId: number) => 0 | 1;
}): ShotQualityEvent[] {
  const { rally, rallyIndex, getTeamFromPlayerId } = args;

  const overlays = [
    ...(Array.isArray(rally?.shotNumberOverlays)
      ? rally.shotNumberOverlays
      : []),
  ].sort((a, b) => (a.rawShot?.start_ms ?? 0) - (b.rawShot?.start_ms ?? 0));

  const out: ShotQualityEvent[] = [];

  for (let i = 0; i < overlays.length; i++) {
    const ov = overlays[i];
    const rawShot = ov?.rawShot;
    const playerId = Number(ov?.playerId ?? rawShot?.player_id);

    if (!Number.isFinite(playerId)) continue;

    const shotStartMs = rawShot?.start_ms;
    const shotEndMs = rawShot?.end_ms ?? rawShot?.start_ms;

    if (
      typeof shotStartMs !== "number" ||
      !Number.isFinite(shotStartMs) ||
      typeof shotEndMs !== "number" ||
      !Number.isFinite(shotEndMs)
    ) {
      continue;
    }

    const q = getOverlayShotQualityParts(ov);

    if (q.overall == null) continue;

    const shotType = rawShot?.shot_type ?? ov?.shotType ?? null;

    out.push({
      rallyIndex,
      shotIndex: i,
      shotNumber: i + 1,
      team: getTeamFromPlayerId(playerId),
      playerId,
      shotStartMs,
      shotEndMs,
      shotType,
      normalizedShotType: normalizeShotType(shotType),
      strokeType: rawShot?.stroke_type ?? null,
      isVolley: !!rawShot?.is_volley,
      isFinal: rawShot?.is_final === true,
      qualityOverall: q.overall,
      qualityExecution: q.execution,
      qualitySelection: q.selection,
      teamWonRally:
        rally.winnerTeam == null || ov?.playerId === null
          ? null
          : rally.winnerTeam === getTeamFromPlayerId(ov?.playerId),
    });

    // out.push({
    //   rallyIndex,
    //   shotIndex: i,
    //   shotNumber: i + 1,
    //   team: getTeamFromPlayerId(playerId),
    //   playerId,
    //   shotStartMs,
    //   shotEndMs,
    //   shotType: rawShot?.shot_type ?? ov?.shotType ?? null,
    //   strokeType: rawShot?.stroke_type ?? null,
    //   isVolley: !!rawShot?.is_volley,
    //   isFinal: rawShot?.is_final === true,
    //   qualityOverall: q.overall,
    //   qualityExecution: q.execution,
    //   qualitySelection: q.selection,
    // });
  }

  return out;
}

export function buildShotQualityClip(args: {
  event: ShotQualityEvent;
  preMs?: number;
  postMs?: number;
  rallyStartMs?: number;
  rallyEndMs?: number;
}): ShotQualityClip {
  const { event } = args;
  const preMs = args.preMs ?? 900;
  const postMs = args.postMs ?? 700;

  let clipStartMs = event.shotStartMs - preMs;
  let clipEndMs = event.shotEndMs + postMs;

  if (typeof args.rallyStartMs === "number") {
    clipStartMs = Math.max(clipStartMs, args.rallyStartMs);
  }
  if (typeof args.rallyEndMs === "number") {
    clipEndMs = Math.min(clipEndMs, args.rallyEndMs);
  }
  if (clipEndMs - clipStartMs < 400) {
    clipEndMs = clipStartMs + 400;
  }

  return {
    key: `shot_quality_r${event.rallyIndex}_s${event.shotIndex}_${Math.round(
      clipStartMs,
    )}_${Math.round(clipEndMs)}`,
    event,
    clipStartMs,
    clipEndMs,
  };
}

// type SortKey = "overall" | "rally";
// type SortDir = "asc" | "desc";

// function sortValue(clip: ShotQualityClip, key: SortKey): number {
//   if (key === "rally")
//     return clip.event.rallyIndex * 1000 + clip.event.shotIndex;
//   return clip.event.qualityOverall ?? Number.POSITIVE_INFINITY;
// }

// 3) expand sorting

type SortKey = "overall" | "rally" | "shotNumber" | "wonRally";
type SortDir = "asc" | "desc";

function sortValue(clip: ShotQualityClip, key: SortKey): number {
  if (key === "rally") {
    return clip.event.rallyIndex * 1000 + clip.event.shotIndex;
  }
  if (key === "shotNumber") {
    return clip.event.shotNumber;
  }
  if (key === "wonRally") {
    if (clip.event.teamWonRally == null) return -1;
    return clip.event.teamWonRally ? 1 : 0;
  }
  return clip.event.qualityOverall ?? Number.POSITIVE_INFINITY;
}

const IMPACT_SUMMARY_SHOT_NUMBERS = [1, 2, 3, 4, 5, 6];

function formatShotNumberRangeLabel(nums: readonly number[]): string {
  if (!nums.length) return "selected shots";

  const sorted = [...nums].sort((a, b) => a - b);
  const contiguous = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);

  if (contiguous) {
    if (sorted.length === 1) return `shot ${sorted[0]}`;
    return `shots ${sorted[0]}–${sorted[sorted.length - 1]}`;
  }

  if (sorted.length <= 3) {
    return `shots ${sorted.join(", ")}`;
  }

  return `selected shots`;
}

export function ShotQualityPopup(props: {
  insightsToCvPlayerIndex: PlayerIndexMap;
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;

  packageId: string;
  activeRallyIndex: number | null;
  rows: KitchenArrivalRow[];

  clipsThisRally: ShotQualityClip[];
  clipsAll: ShotQualityClip[];

  renderPlayerIdentity: RenderPlayerIdentity;
  teamColor: (team: 0 | 1, alpha?: number) => string;

  firefightsClips: FirefightClip[];
  firefightRallySet: Set<number>;
}) {
  const {
    open,
    onClose,
    packageId,
    activeRallyIndex,
    rows,
    clipsAll,
    renderPlayerIdentity,
    firefightsClips,
    firefightRallySet,
    insightsToCvPlayerIndex,
  } = props;

  //   const clips = clipsAll;
  const clips = React.useMemo(() => {
    return clipsAll.filter((c) => c.event.qualityOverall != null);
  }, [clipsAll]);

  //   const [showQualityImpact, setShowQualityImpact] = React.useState(false);

  const [showImpactSummary, setShowImpactSummary] = React.useState(false);
  const [showImpactMethod, setShowImpactMethod] = React.useState(false);

  const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
  const [sortKey, setSortKey] = React.useState<SortKey>("overall");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");
  const [playerFilter, setPlayerFilter] = React.useState<string>("ALL");
  const [shotTypeFilter, setShotTypeFilter] = React.useState<string>("ALL");
  const [wonRallyFilter, setWonRallyFilter] = React.useState<string>("ALL");
  const [shotNumberFilter, setShotNumberFilter] = React.useState<string>("ALL");

  const [pos, setPos] = React.useState<{ x: number; y: number }>({
    x: 18,
    y: 50,
  });

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startPosX: number;
    startPosY: number;
    dragging: boolean;
  } | null>(null);

  const onMouseDownHeader = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startPosX: pos.x,
      startPosY: pos.y,
      dragging: true,
    };
    e.preventDefault();
  };

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d?.dragging) return;

      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      setPos({
        x: Math.max(0, Math.min(window.innerWidth - 220, d.startPosX + dx)),
        y: Math.max(0, Math.min(window.innerHeight - 140, d.startPosY + dy)),
      });
    };

    const onUp = () => {
      if (dragRef.current) dragRef.current.dragging = false;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [pos.x, pos.y]);

  //   const playerIds = React.useMemo(() => {
  //     const s = new Set<number>();
  //     for (const c of clips) s.add(c.event.playerId);
  //     return Array.from(s).sort((a, b) => a - b);
  //   }, [clips]);

  const playerIds = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of clips) s.add(c.event.playerId);
    return Array.from(s).sort((a, b) => a - b);
  }, [clips]);

  const shotTypes = React.useMemo(() => {
    const byNormalized = new Map<string, string>();

    for (const c of clips) {
      const normalized = c.event.normalizedShotType;
      if (!normalized) continue;
      if (!byNormalized.has(normalized)) {
        byNormalized.set(normalized, displayShotType(c.event.shotType));
      }
    }

    return Array.from(byNormalized.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [clips]);

  // optional helper list if you want button filters for shot number
  const shotNumbers = React.useMemo(() => {
    const s = new Set<number>();
    for (const c of clips) s.add(c.event.shotNumber);
    return Array.from(s).sort((a, b) => a - b);
  }, [clips]);

  const selectedShotTypeForImpact =
    shotTypeFilter === "ALL" ? null : shotTypeFilter;

  const impactSummaryByPlayer = React.useMemo(() => {
    return buildImpactSummaryForAllPlayers({
      rows,
      selectedShotType: selectedShotTypeForImpact,
      allowedShotNumbers: IMPACT_SUMMARY_SHOT_NUMBERS,
    });
  }, [rows, selectedShotTypeForImpact]);

  const impactSummaryShotRangeLabel = React.useMemo(() => {
    return formatShotNumberRangeLabel(IMPACT_SUMMARY_SHOT_NUMBERS);
  }, []);

  //   console.log(
  //     "shotTypes",
  //     shotTypes,
  //     "sample normalized",
  //     clips.slice(0, 10).map((c) => ({
  //       shotType: c.event.shotType,
  //       normalizedShotType: c.event.normalizedShotType,
  //     })),
  //   );

  // 5) apply those filters in filteredClips

  const filteredClips = React.useMemo(() => {
    return clips.filter((c) => {
      if (c.event.qualityOverall == null) return false;

      const playerOk =
        playerFilter === "ALL" || String(c.event.playerId) === playerFilter;

      const normalizedShotType = c.event.normalizedShotType || "";
      const shotTypeOk =
        shotTypeFilter === "ALL" || normalizedShotType === shotTypeFilter;

      const shotNumberOk =
        shotNumberFilter === "ALL" ||
        c.event.shotNumber === Number(shotNumberFilter);

      const wonRallyOk =
        wonRallyFilter === "ALL" ||
        (wonRallyFilter === "WON" && c.event.teamWonRally === true) ||
        (wonRallyFilter === "LOST" && c.event.teamWonRally === false);

      return playerOk && shotTypeOk && shotNumberOk && wonRallyOk;
    });
  }, [clips, playerFilter, shotTypeFilter, shotNumberFilter, wonRallyFilter]);

  const sortedClips = React.useMemo(() => {
    const arr = [...filteredClips];
    arr.sort((a, b) => {
      const d = sortValue(a, sortKey) - sortValue(b, sortKey);
      return sortDir === "asc" ? d : -d;
    });
    return arr;
  }, [filteredClips, sortKey, sortDir]);

  React.useEffect(() => {
    if (!open) return;
    setSelectedKey(sortedClips[0]?.key ?? null);
  }, [open, activeRallyIndex, playerFilter, shotTypeFilter, sortKey, sortDir]);

  const selected =
    sortedClips.find((c) => c.key === selectedKey) ?? sortedClips[0] ?? null;

  const selectedRow = React.useMemo(() => {
    if (!selected) return null;
    return rows[selected.event.rallyIndex] ?? null;
  }, [rows, selected?.key]);

  const clipUrl = React.useMemo(() => {
    if (!selected) return null;
    return buildRallySegmentUrl({
      packageId,
      rallyIndex: selected.event.rallyIndex,
      startMs: Math.round(selected.clipStartMs),
      endMs: Math.round(selected.clipEndMs),
      download: 0,
    });
  }, [packageId, selected?.key]);

  const clipRow = React.useMemo(() => {
    if (!selectedRow || !selected) return null;
    return selectedRow;
  }, [selectedRow, selected?.key]);

  const [seedNonce, setSeedNonce] = React.useState(0);

  React.useEffect(() => {
    if (!selected) return;
    setSeedNonce((n) => n + 1);
  }, [
    selected?.event?.rallyIndex,
    selected?.event?.shotIndex,
    Math.round(selected?.clipStartMs ?? 0),
    Math.round(selected?.clipEndMs ?? 0),
  ]);

  const selectedLoopRegion = React.useMemo(() => {
    if (!selectedRow || !selected) return undefined;
    const segmentStartMs = selectedRow.segmentStartMs ?? selectedRow.startMs ?? 0;
    return {
      enabled: true,
      aSec: Math.max(0, (selected.clipStartMs - segmentStartMs) / 1000),
      bSec: Math.max(0.5, (selected.clipEndMs - segmentStartMs) / 1000),
      nonce: seedNonce,
    };
  }, [selectedRow, selected?.key, seedNonce]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "overall" ? "desc" : "asc");
    }
  };

  const selectMenuProps = React.useMemo(
    () => ({
      PaperProps: {
        sx: {
          zIndex: 2600,
        },
      },
    }),
    [],
  );

  const filterButtonSx = {
    minWidth: 0,
    px: 0.85,
    py: 0.32,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    textTransform: "none" as const,
    borderColor: "rgba(25, 118, 210, 0.55)",
    color: "rgba(25, 118, 210, 0.92)",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(25, 118, 210, 0.8)",
      backgroundColor: "rgba(25, 118, 210, 0.05)",
      boxShadow: "none",
    },
  };

  const selectedFilterButtonSx = {
    ...filterButtonSx,
    backgroundColor: "rgba(25, 118, 210, 0.14)",
    color: "rgba(25, 118, 210, 0.98)",
    borderColor: "rgba(25, 118, 210, 0.42)",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.18)",
      borderColor: "rgba(25, 118, 210, 0.52)",
      boxShadow: "none",
    },
  };

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: "calc(100vw - 90px)",
        maxWidth: "calc(100vw - 90px)",
        height: "calc(100vh - 110px)",
        zIndex: 1800,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2,
      }}
    >
      <Box
        onMouseDown={onMouseDownHeader}
        sx={{
          cursor: "move",
          px: 1.25,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(0,0,0,0.12)",
          bgcolor: "rgba(245,245,245,0.96)",
        }}
      >
        {/* <Box sx={{ fontWeight: 900, fontSize: 16 }}>Shot Quality</Box> */}

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
          <StarIcon sx={{ fontSize: 20, color: "rgba(235, 200, 90, 0.95)" }} />
          <Box sx={{ fontWeight: 900, fontSize: 16 }}>Shot Quality</Box>
        </Box>

        <Button
          size="small"
          onClick={onClose}
          startIcon={<CloseIcon />}
          sx={{ minWidth: 0 }}
        >
          Close
        </Button>
      </Box>

      <Box sx={{ display: "flex", minHeight: 0, flex: 1 }}>
        <Box
          sx={{
            width: 470,
            minWidth: 470,
            borderRight: "1px solid rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}> */}

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              px: 1.1,
              pt: 0.65,
              pb: 0.45,
              maxHeight: 3000,
              overflowY: "auto",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.72)" }}
            >
              Player
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setPlayerFilter("ALL")}
                sx={
                  playerFilter === "ALL"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {playerIds.map((id) => (
                <Button
                  key={id}
                  size="small"
                  variant="outlined"
                  onClick={() => setPlayerFilter(String(id))}
                  sx={
                    playerFilter === String(id)
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 24,
                    }}
                  >
                    {renderPlayerIdentity({
                      playerId: id,
                      size: 36,
                      fontSize: 12,
                    })}
                  </Box>
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(0,0,0,0.72)",
              }}
            >
              Shot type
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShotTypeFilter("ALL")}
                sx={
                  shotTypeFilter === "ALL"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {shotTypes.map((t) => (
                <Button
                  key={t.value}
                  size="small"
                  variant="outlined"
                  onClick={() => setShotTypeFilter(t.value)}
                  sx={
                    shotTypeFilter === t.value
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  {t.label}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(0,0,0,0.72)",
              }}
            >
              Shot number
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShotNumberFilter("ALL")}
                sx={
                  shotNumberFilter === "ALL"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {shotNumbers.map((n) => (
                <Button
                  key={n}
                  size="small"
                  variant="outlined"
                  onClick={() => setShotNumberFilter(String(n))}
                  sx={
                    shotNumberFilter === String(n)
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  {n}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(0,0,0,0.72)",
              }}
            >
              Rally result
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setWonRallyFilter("ALL")}
                sx={
                  wonRallyFilter === "ALL"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setWonRallyFilter("WON")}
                sx={
                  wonRallyFilter === "WON"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                Won
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setWonRallyFilter("LOST")}
                sx={
                  wonRallyFilter === "LOST"
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                Lost
              </Button>
            </Box>
          </Box>
          {/* <Box
            sx={{
              p: 1,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              borderBottom: "1px solid rgba(0,0,0,0.10)",
            }}
          > */}
          {/* <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                select
                size="small"
                label="Player"
                value={playerFilter}
                onChange={(e) => setPlayerFilter(String(e.target.value))}
                sx={{ flex: 1 }}
                 SelectProps={{ MenuProps: selectMenuProps }}

              >
                <MenuItem value="ALL">All players</MenuItem>
                {playerIds.map((id) => (
                  <MenuItem key={id} value={String(id)}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        minHeight: 24,
                      }}
                    >
                      {renderPlayerIdentity({
                        playerId: id,
                        size: 22,
                        fontSize: 12,
                        //borderRadius: "50%",
                      })}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                size="small"
                label="Shot type"
                value={shotTypeFilter}
                onChange={(e) => setShotTypeFilter(String(e.target.value))}
                sx={{ flex: 1 }}
                                 SelectProps={{ MenuProps: selectMenuProps }}

              >
                <MenuItem value="ALL">All shot types</MenuItem>
                {shotTypes.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>


            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant={sortKey === "overall" ? "contained" : "outlined"}
                onClick={() => toggleSort("overall")}
              >
                Quality{" "}
                {sortKey === "overall" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </Button>

              <Button
                size="small"
                variant={sortKey === "rally" ? "contained" : "outlined"}
                onClick={() => toggleSort("rally")}
              >
                Rally{" "}
                {sortKey === "rally" ? (sortDir === "asc" ? "↑" : "↓") : ""}
              </Button>
            </Box> */}

          {/* </Box> */}

          {/* <Box
            sx={{
              mx: 0.75,
              mt: 0.65,
              mb: 0.55,
              px: 1,
              py: 0.8,
              borderRadius: 1,
              border: "1px solid rgba(0,0,0,0.12)",
              bgcolor: "rgba(245,245,245,0.82)",
              display: "flex",
              flexDirection: "column",
              gap: 0.65,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: 12, fontWeight: 800 }}>
                  Where shot quality most affected rally wins in {impactSummaryShotRangeLabel}
                </Box>{" "}
                <Box
                  sx={{
                    mt: 0.2,
                    fontSize: 11.5,
                    lineHeight: 1.35,
                    color: "rgba(0,0,0,0.72)",
                  }}
                >

                    It shows which shot numbers in this game had the strongest relationship between shot quality and rally wins — meaning where better shot quality, or drops in shot quality, most changed the chance of winning the rally.

                </Box>
              </Box>

              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowImpactMethod((v) => !v)}
                sx={{
                  minWidth: 0,
                  px: 0.85,
                  py: 0.3,
                  fontSize: 11,
                  fontWeight: 700,
                  lineHeight: 1,
                  textTransform: "none",
                  flexShrink: 0,
                }}
              >
                {showImpactMethod ? "Hide details" : "Details"}
              </Button>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                columnGap: 0.75,
                rowGap: 0.55,
                alignItems: "center",
              }}
            >
              {impactSummaryByPlayer.map((player) => {
                const playerIsSelected =
                  playerFilter !== "ALL" &&
                  Number(playerFilter) === player.playerId;

                return (
                  <React.Fragment key={player.playerId}>
                    <Box
                      sx={{
                        opacity:
                          playerFilter === "ALL" || playerIsSelected ? 1 : 0.55,
                      }}
                    >
                      {renderPlayerIdentity({
                        playerId: player.playerId,
                        size: 30,
                        fontSize: 12,
                      })}
                    </Box>

                    <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: playerIsSelected ? 800 : 700,
                        color:
                          playerFilter === "ALL" || playerIsSelected
                            ? "rgba(0,0,0,0.85)"
                            : "rgba(0,0,0,0.58)",
                      }}
                    >
                      {summarizePlayerImpact(player.rows)}
                    </Box>
                  </React.Fragment>
                );
              })}
            </Box>

            {showImpactMethod ? (
              <Box
                sx={{
                  mt: 0.15,
                  pt: 0.7,
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.55,
                }}
              >
                <Box
                  sx={{
                    fontSize: 11.5,
                    lineHeight: 1.4,
                    color: "rgba(0,0,0,0.74)",
                  }}
                >
                  <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                    What this is doing
                  </Box>
                  For each player, it looks only at shots 3–6, then for each
                  shot number separately compares shot quality with whether that
                  player&apos;s team won the rally.
                </Box>

                <Box
                  sx={{
                    fontSize: 11.5,
                    lineHeight: 1.4,
                    color: "rgba(0,0,0,0.74)",
                  }}
                >
                  <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                    Modeling approach
                  </Box>
                  It fits a simple 1D logistic regression for each player + shot
                  number:
                  <Box
                    sx={{
                      mt: 0.35,
                      px: 0.7,
                      py: 0.45,
                      borderRadius: 0.75,
                      bgcolor: "rgba(0,0,0,0.045)",
                      fontFamily: "monospace",
                      fontSize: 11,
                    }}
                  >
                    P(win rally) = sigmoid(a + b × shotQuality)
                  </Box>
                </Box>

                <Box
                  sx={{
                    fontSize: 11.5,
                    lineHeight: 1.4,
                    color: "rgba(0,0,0,0.74)",
                  }}
                >
                  <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                    Current ranking logic
                  </Box>
                  Internally it still ranks shot numbers using the fitted
                  curve&apos;s change from quality 0.50 to 0.60. That hidden
                  baseline is one reason the exact numeric values are not shown
                  here.
                </Box>

                <Box
                  sx={{
                    fontSize: 11.5,
                    lineHeight: 1.4,
                    color: "rgba(0,0,0,0.74)",
                  }}
                >
                  <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                    What it is not claiming
                  </Box>
                  This is not causal, does not adjust for opponent pressure or
                  full rally context, and is best read as a rough within-game
                  ranking of where quality differences seemed to matter most.
                </Box>
              </Box>
            ) : null}
          </Box> */}

          <Box
            sx={{
              mx: 0.75,
              mt: 0.65,
              mb: 0.55,
              display: "flex",
              flexDirection: "column",
              gap: 0.6,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                flexWrap: "wrap",
              }}
            >
              <Button
                size="small"
                variant={showImpactSummary ? "contained" : "outlined"}
                onClick={() => {
                  setShowImpactSummary((v) => !v);
                  if (showImpactSummary) {
                    setShowImpactMethod(false);
                  }
                }}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.38,
                  fontSize: 12,
                  fontWeight: 700,
                  lineHeight: 1,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                Where shot quality mattered most ({impactSummaryShotRangeLabel})
              </Button>

              {/* <Box sx={{ fontSize: 11.5, color: "rgba(0,0,0,0.6)" }}>
                {impactSummaryShotRangeLabel}
              </Box> */}
            </Box>

            {showImpactSummary ? (
              <Box
                sx={{
                  px: 1,
                  py: 0.8,
                  borderRadius: 1,
                  border: "1px solid rgba(0,0,0,0.12)",
                  bgcolor: "rgba(245,245,245,0.82)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.65,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ fontSize: 12, fontWeight: 800 }}>
                      Where shot quality most affected rally wins in{" "}
                      {impactSummaryShotRangeLabel}
                    </Box>
                    <Box
                      sx={{
                        mt: 0.2,
                        fontSize: 11.5,
                        lineHeight: 1.35,
                        color: "rgba(0,0,0,0.72)",
                      }}
                    >
                      It shows which shot numbers in this game had the strongest
                      relationship between shot quality and rally wins — meaning
                      where better shot quality, or drops in shot quality, most
                      changed the chance of winning the rally.
                    </Box>
                  </Box>

                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowImpactMethod((v) => !v)}
                    sx={{
                      minWidth: 0,
                      px: 0.85,
                      py: 0.3,
                      fontSize: 11,
                      fontWeight: 700,
                      lineHeight: 1,
                      textTransform: "none",
                      flexShrink: 0,
                    }}
                  >
                    {showImpactMethod ? "Hide details" : "Details"}
                  </Button>
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr",
                    columnGap: 0.75,
                    rowGap: 0.55,
                    alignItems: "center",
                  }}
                >
                  {impactSummaryByPlayer.map((player) => {
                    const playerIsSelected =
                      playerFilter !== "ALL" &&
                      Number(playerFilter) === player.playerId;

                    return (
                      <React.Fragment key={player.playerId}>
                        <Box
                          sx={{
                            opacity:
                              playerFilter === "ALL" || playerIsSelected
                                ? 1
                                : 0.55,
                          }}
                        >
                          {renderPlayerIdentity({
                            playerId: player.playerId,
                            size: 30,
                            fontSize: 12,
                          })}
                        </Box>

                        <Box
                          sx={{
                            fontSize: 12,
                            fontWeight: playerIsSelected ? 800 : 700,
                            color:
                              playerFilter === "ALL" || playerIsSelected
                                ? "rgba(0,0,0,0.85)"
                                : "rgba(0,0,0,0.58)",
                          }}
                        >
                          {summarizePlayerImpact(player.rows)}
                        </Box>
                      </React.Fragment>
                    );
                  })}
                </Box>

                {showImpactMethod ? (
                  <Box
                    sx={{
                      mt: 0.15,
                      pt: 0.7,
                      borderTop: "1px solid rgba(0,0,0,0.08)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.55,
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: 11.5,
                        lineHeight: 1.4,
                        color: "rgba(0,0,0,0.74)",
                      }}
                    >
                      <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                        What this is doing
                      </Box>
                      For each player, it looks only at{" "}
                      {impactSummaryShotRangeLabel}, then for each shot number
                      separately compares shot quality with whether that
                      player&apos;s team won the rally.
                    </Box>

                    <Box
                      sx={{
                        fontSize: 11.5,
                        lineHeight: 1.4,
                        color: "rgba(0,0,0,0.74)",
                      }}
                    >
                      <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                        Modeling approach
                      </Box>
                      It fits a simple 1D logistic regression for each player +
                      shot number:
                      <Box
                        sx={{
                          mt: 0.35,
                          px: 0.7,
                          py: 0.45,
                          borderRadius: 0.75,
                          bgcolor: "rgba(0,0,0,0.045)",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}
                      >
                        P(win rally) = sigmoid(a + b × shotQuality)
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        fontSize: 11.5,
                        lineHeight: 1.4,
                        color: "rgba(0,0,0,0.74)",
                      }}
                    >
                      <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                        Current ranking logic
                      </Box>
                      Internally it still ranks shot numbers using the fitted
                      curve&apos;s change from quality 0.50 to 0.60, while also
                      checking the downward direction from 0.50 to 0.40 and
                      using whichever direction is stronger.
                    </Box>

                    <Box
                      sx={{
                        fontSize: 11.5,
                        lineHeight: 1.4,
                        color: "rgba(0,0,0,0.74)",
                      }}
                    >
                      <Box sx={{ fontWeight: 800, color: "rgba(0,0,0,0.84)" }}>
                        What it is not claiming
                      </Box>
                      This is not causal, does not adjust for opponent pressure
                      or full rally context, and is best read as a rough
                      within-game ranking of where shot-quality differences
                      seemed to matter most.
                    </Box>
                  </Box>
                ) : null}
              </Box>
            ) : null}
          </Box>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell
                    onClick={() => toggleSort("rally")}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Rally{" "}
                    {sortKey === "rally" ? (sortDir === "asc" ? "↑" : "↓") : ""}
                  </TableCell>

                  <TableCell
                    onClick={() => toggleSort("shotNumber")}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Shot{" "}
                    {sortKey === "shotNumber"
                      ? sortDir === "asc"
                        ? "↑"
                        : "↓"
                      : ""}
                  </TableCell>

                  <TableCell>Player</TableCell>
                  <TableCell>Type</TableCell>

                  <TableCell
                    onClick={() => toggleSort("wonRally")}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    Won{" "}
                    {sortKey === "wonRally"
                      ? sortDir === "asc"
                        ? "↑"
                        : "↓"
                      : ""}
                  </TableCell>

                  <TableCell
                    align="right"
                    onClick={() => toggleSort("overall")}
                    sx={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        gap: 0.45,
                        width: "100%",
                      }}
                    >
                      <StarIcon
                        sx={{ fontSize: 12, color: "rgba(235, 200, 90, 0.88)" }}
                      />
                      <Box component="span">
                        Quality{" "}
                        {sortKey === "overall"
                          ? sortDir === "asc"
                            ? "↑"
                            : "↓"
                          : ""}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {sortedClips.map((clip) => {
                  const selectedRowKey = selected?.key === clip.key;

                  return (
                    <TableRow
                      key={clip.key}
                      hover
                      selected={selectedRowKey}
                      onClick={() => setSelectedKey(clip.key)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{clip.event.rallyIndex + 1}</TableCell>
                      <TableCell>{clip.event.shotNumber}</TableCell>

                      {/* <TableCell>{`P${clip.event.playerId}`}</TableCell> */}

                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minHeight: 24,
                          }}
                        >
                          {renderPlayerIdentity({
                            playerId: clip.event.playerId,
                            size: 36,
                            fontSize: 12,
                            //borderRadius: "50%",
                          })}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {displayShotType(clip.event.shotType)}
                      </TableCell>

                      <TableCell>
                        {clip.event.teamWonRally == null
                          ? "—"
                          : clip.event.teamWonRally
                            ? "Won"
                            : "Lost"}
                      </TableCell>

                      <TableCell align="right">
                        {clip.event.qualityOverall != null
                          ? clip.event.qualityOverall.toFixed(2)
                          : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {!sortedClips.length ? (
              <Box sx={{ p: 2, opacity: 0.7 }}>
                No shots for this selection.
              </Box>
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              pt: 1,
              pb: 1,
              pl: 1.1,
              pr: 0.35,
              borderBottom: "1px solid rgba(0,0,0,0.12)",
            }}
          >
            {selected ? (

              <Typography sx={{ fontWeight: 900 }}>
                Rally {selected.event.rallyIndex + 1} • Shot{" "}
                {selected.event.shotNumber}
                {selected.event.shotType
                  ? ` • ${displayShotType(selected.event.shotType)}`
                  : ""}
                {selected.event.teamWonRally == null
                  ? ""
                  : selected.event.teamWonRally
                    ? " • Team won rally"
                    : " • Team lost rally"}
                {" • "}
                {formatShotQualityEvent(selected.event)}
              </Typography>
            ) : (
              <Typography sx={{ opacity: 0.75 }}>No shot selected.</Typography>
            )}
          </Box>

          <Box sx={{ pl: 0.8, pr: 0.15 }}>
            <CondensedRallyTimeline_ContextOnly
              activeRally={selectedRow}
              rows={rows}
              barsAreaHeightPx={50}
              colW={12}
              showLegend={false}
              showRallyIndex={true}
              showWinnerAndServe={true}
              firefightRallySet={firefightRallySet}
            />
          </Box>

          <Box
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              bgcolor: "black",
              borderBottom: "1px solid rgba(255,255,255,0.10)",
              overflow: "hidden",
            }}
          >
            {selected && clipRow ? (
              <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    px: 0,
                    display: "flex",
                    alignItems: "stretch",
                    justifyContent: "stretch",
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                    <ActiveRallyVideo
                      insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                      renderPlayerIdentity={renderPlayerIdentity}
                      aiContextLabel="Shot Quality"
                      focusedFilter={null}
                      packageId={packageId}
                      row={clipRow}
                      rows={rows}
                      activeShotNumber={selected.event.shotNumber}
                      activeShotExtraText={undefined}
                      shotOverlayPersistentExtraText={formatOverlayShotQuality}
                      showCondensedTimeline={false}
                      fontColor="white"
                      controlsPlacement="left"
                      firefightsClips={firefightsClips}
                      firefightRallySet={firefightRallySet}
                      showRateControls={true}
                      showTitle={false}
                      showScoreEtc={false}
                      showAi={true}
                      aiDrawerMode="split"
                      showLoopScrubber={true}
                      defaultLoopUiOpen={false}
                      showPopoutButton={true}
                      isInFullPopup={false}
                      seedPlayback={{
                        timeSec: selectedLoopRegion?.aSec ?? 0,
                        paused: false,
                        nonce: seedNonce,
                      }}
                      seedLoopRegion={selectedLoopRegion}
                      seekToActiveShotOnChange={false}
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    position: "absolute",
                    left: 10,
                    top: 10,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: "rgba(0,0,0,0.65)",
                    color: "white",
                    fontSize: 12,
                    lineHeight: 1.25,
                    maxWidth: "70%",
                    pointerEvents: "none",
                  }}
                >
                  <Box sx={{ fontWeight: 900, fontSize: 13 }}>
                    Shot {selected.event.shotNumber}
                  </Box>
                  <Box>{formatShotQualityEvent(selected.event)}</Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ p: 2, color: "white", opacity: 0.75 }}>
                No clip selected.
              </Box>
            )}
          </Box>

          {/* <Box sx={{ position: "relative", minHeight: 0, flex: 1 }}> */}
          {/* <Box
            sx={{
              position: "relative",
              minHeight: 0,
              flex: "1 1 auto",
              overflow: "hidden",
            }}
          >

            {selectedRow && selected  ? (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  px: 0,
                  display: "flex",
                  alignItems: "stretch",
                  justifyContent: "stretch",
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                  <ActiveRallyVideo
                    insightsToCvPlayerIndex={insightsToCvPlayerIndex}
                    renderPlayerIdentity={renderPlayerIdentity}
                    aiContextLabel="Shot Quality"
                    focusedFilter={null}
                    packageId={packageId}
                    row={selectedRow}
                    rows={rows}
                    activeShotNumber={selected.event.shotNumber}
                    activeShotExtraText={undefined}
                    shotOverlayPersistentExtraText={formatOverlayShotQuality}
                    showCondensedTimeline={false}
                    fontColor="white"
                    controlsPlacement="left"
                    firefightsClips={firefightsClips}
                    firefightRallySet={firefightRallySet}
                    showRateControls={true}
                    showTitle={false}
                    showScoreEtc={false}
                    showAi={true}
                    aiDrawerMode="split"
                    showLoopScrubber={true}
                    defaultLoopUiOpen={false}
                    showPopoutButton={true}
                    isInFullPopup={false}
                    seedPlayback={{
                      timeSec:
                        Math.max(
                          0,
                          (selected.event.shotStartMs -
                            selectedRow.segmentStartMs) /
                            1000,
                        ) || 0,
                      paused: false,
                      nonce: seedNonce,
                    }}
                  />
                </Box>
              </Box>
            ) : null}
          </Box> */}
        </Box>
      </Box>

      {/* {showQualityImpact && (
        <ShotQualityImpactPopup
          open={showQualityImpact}
          onClose={() => setShowQualityImpact(false)}
          rows={rows}
          renderPlayerIdentity={renderPlayerIdentity}
          initialFilters={{
            selectedPlayerId:
              playerFilter === "ALL" ? null : Number(playerFilter),
            selectedShotType: shotTypeFilter === "ALL" ? null : shotTypeFilter,
            selectedShotNumber:
              shotNumberFilter === "ALL" ? null : Number(shotNumberFilter),
          }}
        />
      )} */}
    </Paper>
  );
}

type InlineImpactSummaryRow = {
  shotNumber: number;
  n: number;
  deltaPerPointOne: number | null;
  label:
    | "strongest signal"
    | "moderate signal"
    | "weaker signal"
    | "no clear signal"
    | "not enough data";
};

type PlayerInlineImpactSummary = {
  playerId: number;
  rows: InlineImpactSummaryRow[];
};

function ordinalShotLabel(n: number): string {
  if (n === 1) return "1st shot";
  if (n === 2) return "2nd shot";
  if (n === 3) return "3rd shot";
  return `${n}th shot`;
}

function buildInlineImpactSummaryRows(args: {
  rows: KitchenArrivalRow[];
  selectedPlayerId: number;
  selectedShotType: string | null;
  allowedShotNumbers?: number[];
}): InlineImpactSummaryRow[] {
  const {
    rows,
    selectedPlayerId,
    selectedShotType,
    allowedShotNumbers = [3, 4, 5, 6],
  } = args;

  const allowed = new Set(allowedShotNumbers);
  const byShotNumber = new Map<number, QualityOutcomePair[]>();

  for (let rallyIndex = 0; rallyIndex < rows.length; rallyIndex++) {
    const r = rows[rallyIndex];
    if (!r) continue;

    const events = computeShotQualityEventsForRally({
      rally: r,
      rallyIndex,
      getTeamFromPlayerId: (playerId: number) =>
        playerId === 0 || playerId === 1 ? 0 : 1,
    });

    for (const ev of events) {
      if (!allowed.has(ev.shotNumber)) continue;
      if (ev.playerId !== selectedPlayerId) continue;

      if (
        selectedShotType != null &&
        ev.normalizedShotType !== selectedShotType.toLowerCase()
      ) {
        continue;
      }

      if (ev.qualityOverall == null) continue;
      if (ev.teamWonRally == null) continue;

      const arr = byShotNumber.get(ev.shotNumber) ?? [];
      arr.push({
        q: ev.qualityOverall,
        win: ev.teamWonRally ? 1 : 0,
      });
      byShotNumber.set(ev.shotNumber, arr);
    }
  }

  const out: InlineImpactSummaryRow[] = [];

  for (const shotNumber of allowedShotNumbers) {
    const pairs = byShotNumber.get(shotNumber) ?? [];
    const xs = pairs.map((p) => p.q);
    const ys = pairs.map((p) => p.win);

    if (xs.length < 6) {
      out.push({
        shotNumber,
        n: xs.length,
        deltaPerPointOne: null,
        label: "not enough data",
      });
      continue;
    }

    const { a, b } = logisticRegression1D(xs, ys);
    const predict = (q: number) => 1 / (1 + Math.exp(-(a + b * q)));
    // const deltaPerPointOne = predict(0.6) - predict(0.5);

    const deltaUp = predict(0.6) - predict(0.5);
    const deltaDown = predict(0.5) - predict(0.4);

    // choose whichever direction is stronger
    const deltaPerPointOne =
      Math.abs(deltaDown) > Math.abs(deltaUp) ? -deltaDown : deltaUp;

    let label: InlineImpactSummaryRow["label"];
    if (deltaPerPointOne <= 0) {
      label = "no clear signal";
    } else if (deltaPerPointOne >= 0.04) {
      label = "strongest signal";
    } else if (deltaPerPointOne >= 0.015) {
      label = "moderate signal";
    } else {
      label = "weaker signal";
    }

    out.push({
      shotNumber,
      n: xs.length,
      deltaPerPointOne,
      label,
    });
  }

  out.sort((a, b) => {
    const av = a.deltaPerPointOne ?? Number.NEGATIVE_INFINITY;
    const bv = b.deltaPerPointOne ?? Number.NEGATIVE_INFINITY;
    return bv - av;
  });

  return out;
}

function buildImpactSummaryForAllPlayers(args: {
  rows: KitchenArrivalRow[];
  selectedShotType: string | null;
  allowedShotNumbers?: number[];
}): PlayerInlineImpactSummary[] {
  const { rows, selectedShotType, allowedShotNumbers = [3, 4, 5, 6] } = args;

  return [0, 1, 2, 3].map((playerId) => ({
    playerId,
    rows: buildInlineImpactSummaryRows({
      rows,
      selectedPlayerId: playerId,
      selectedShotType,
      allowedShotNumbers,
    }),
  }));
}

function summarizePlayerImpact(rows: InlineImpactSummaryRow[]): string {
  const usable = rows.filter((r) => r.label !== "not enough data");

  if (!usable.length) return "no clear signal yet";

  const strongest = usable[0];
  const second = usable[1];

  if (!strongest || strongest.deltaPerPointOne == null) {
    return "no clear signal yet";
  }

  const firstLabel = ordinalShotLabel(strongest.shotNumber);
  const secondLabel = second ? ordinalShotLabel(second.shotNumber) : null;

  if (strongest.deltaPerPointOne < 0) {
    return secondLabel
      ? `drops in shot quality were most tied to losing on ${firstLabel}, then ${secondLabel}`
      : `drops in shot quality were most tied to losing on ${firstLabel}`;
    //   return secondLabel
    //   ? `execution drop-offs were most tied to losing on ${firstLabel}, then ${secondLabel}`
    //   : `execution drop-offs were most tied to losing on ${firstLabel}`;
  }

  return secondLabel
    ? `better shot quality was most tied to winning on ${firstLabel}, then ${secondLabel}`
    : `better shot quality was most tied to winning on ${firstLabel}`;
  //   return secondLabel
  //     ? `better execution was most tied to winning on ${firstLabel}, then ${secondLabel}`
  //     : `better execution was most tied to winning on ${firstLabel}`;
}

function summarizePlayerImpact_old(rows: InlineImpactSummaryRow[]): string {
  const usable = rows.filter(
    (r) => r.label !== "not enough data" && r.label !== "no clear signal",
  );

  if (!usable.length) return "no clear signal yet";

  const strongest = usable[0];
  const second = usable[1];

  if (!strongest) return "no clear signal yet";

  const firstLabel = ordinalShotLabel(strongest.shotNumber);

  if (!second) {
    return `${firstLabel} stood out most`;
  }

  const secondLabel = ordinalShotLabel(second.shotNumber);

  const directionWord =
    strongest.deltaPerPointOne != null && strongest.deltaPerPointOne < 0
      ? "drops in quality hurt most on"
      : "higher-quality shots helped most on";

  return `${directionWord} ${firstLabel}, followed by ${secondLabel}`;
}

function ShotQualityImpactPopup(props: {
  open: boolean;
  onClose: () => void;
  rows: KitchenArrivalRow[];
  renderPlayerIdentity: RenderPlayerIdentity;
  initialFilters: ShotQualityImpactFilters;
}) {
  const { open, onClose, rows, renderPlayerIdentity, initialFilters } = props;

  const [impactPlayerFilter, setImpactPlayerFilter] = React.useState<
    number | null
  >(initialFilters.selectedPlayerId);
  const [impactShotTypeFilter, setImpactShotTypeFilter] = React.useState<
    string | null
  >(initialFilters.selectedShotType);
  const [impactShotNumberFilter, setImpactShotNumberFilter] = React.useState<
    number | null
  >(initialFilters.selectedShotNumber);

  const allEvents = React.useMemo(() => {
    const out: ShotQualityEvent[] = [];

    for (let rallyIndex = 0; rallyIndex < rows.length; rallyIndex++) {
      const r = rows[rallyIndex];
      if (!r) continue;

      out.push(
        ...computeShotQualityEventsForRally({
          rally: r,
          rallyIndex,
          getTeamFromPlayerId: (playerId: number) =>
            playerId === 0 || playerId === 1 ? 0 : 1,
        }),
      );
    }

    return out;
  }, [rows]);

  const playerIds = React.useMemo(() => {
    const s = new Set<number>();
    for (const ev of allEvents) s.add(ev.playerId);
    return Array.from(s).sort((a, b) => a - b);
  }, [allEvents]);

  const shotTypes = React.useMemo(() => {
    const byNormalized = new Map<string, string>();
    for (const ev of allEvents) {
      if (!ev.normalizedShotType) continue;
      if (!byNormalized.has(ev.normalizedShotType)) {
        byNormalized.set(ev.normalizedShotType, displayShotType(ev.shotType));
      }
    }

    return Array.from(byNormalized.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allEvents]);

  const shotNumbers = React.useMemo(() => {
    const s = new Set<number>();
    for (const ev of allEvents) s.add(ev.shotNumber);
    return Array.from(s).sort((a, b) => a - b);
  }, [allEvents]);

  const filters = React.useMemo(
    () => ({
      selectedPlayerId: impactPlayerFilter,
      selectedShotType: impactShotTypeFilter,
      selectedShotNumber: impactShotNumberFilter,
    }),
    [impactPlayerFilter, impactShotTypeFilter, impactShotNumberFilter],
  );

  const [rect, setRect] = React.useState({
    x: 500,
    y: 180,
    w: 860,
    h: 560,
  });

  const filterButtonSx = {
    minWidth: 0,
    px: 0.85,
    py: 0.32,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    textTransform: "none" as const,
    borderColor: "rgba(25, 118, 210, 0.55)",
    color: "rgba(25, 118, 210, 0.92)",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(25, 118, 210, 0.8)",
      backgroundColor: "rgba(25, 118, 210, 0.05)",
      boxShadow: "none",
    },
  };

  const selectedFilterButtonSx = {
    ...filterButtonSx,
    backgroundColor: "rgba(25, 118, 210, 0.14)",
    color: "rgba(25, 118, 210, 0.98)",
    borderColor: "rgba(25, 118, 210, 0.42)",
    "&:hover": {
      backgroundColor: "rgba(25, 118, 210, 0.18)",
      borderColor: "rgba(25, 118, 210, 0.52)",
      boxShadow: "none",
    },
  };
  if (!open) return null;

  return (
    <FloatingPanel
      open={open}
      rect={rect}
      onChangeRect={setRect}
      onClose={onClose}
      title="Shot Quality → Rally Outcome Impact"
      minW={760}
      minH={460}
      color="black"
    >
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: "white",
        }}
      >
        <Box
          sx={{
            px: 1.25,
            py: 0.9,
            borderBottom: "1px solid rgba(0,0,0,0.10)",
            display: "flex",
            flexWrap: "wrap",
            gap: 0.75,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              px: 1.1,
              pt: 0.65,
              pb: 0.45,
              borderBottom: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            <Box
              sx={{ fontSize: 12, fontWeight: 700, color: "rgba(0,0,0,0.72)" }}
            >
              Player
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setImpactPlayerFilter(null)}
                sx={
                  impactPlayerFilter == null
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {playerIds.map((id) => (
                <Button
                  key={id}
                  size="small"
                  variant="outlined"
                  onClick={() => setImpactPlayerFilter(id)}
                  sx={
                    impactPlayerFilter === id
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minHeight: 24,
                    }}
                  >
                    {renderPlayerIdentity({
                      playerId: id,
                      size: 36,
                      fontSize: 12,
                    })}
                  </Box>
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(0,0,0,0.72)",
              }}
            >
              Shot type
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setImpactShotTypeFilter(null)}
                sx={
                  impactShotTypeFilter == null
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {shotTypes.map((t) => (
                <Button
                  key={t.value}
                  size="small"
                  variant="outlined"
                  onClick={() => setImpactShotTypeFilter(t.value)}
                  sx={
                    impactShotTypeFilter === t.value
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  {t.label}
                </Button>
              ))}
            </Box>

            <Box
              sx={{
                mt: 0.5,
                fontSize: 12,
                fontWeight: 700,
                color: "rgba(0,0,0,0.72)",
              }}
            >
              Shot number
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setImpactShotNumberFilter(null)}
                sx={
                  impactShotNumberFilter == null
                    ? selectedFilterButtonSx
                    : filterButtonSx
                }
              >
                All
              </Button>

              {shotNumbers.map((n) => (
                <Button
                  key={n}
                  size="small"
                  variant="outlined"
                  onClick={() => setImpactShotNumberFilter(n)}
                  sx={
                    impactShotNumberFilter === n
                      ? selectedFilterButtonSx
                      : filterButtonSx
                  }
                >
                  {n}
                </Button>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
          <ImpactChart rows={rows} filters={filters} />
        </Box>
      </Box>
    </FloatingPanel>
  );
}

function extractQualityOutcomePairs(
  rows: KitchenArrivalRow[],
  filters: ShotQualityImpactFilters,
): QualityOutcomePair[] {
  const out: QualityOutcomePair[] = [];

  for (let rallyIndex = 0; rallyIndex < rows.length; rallyIndex++) {
    const r = rows[rallyIndex];
    if (!r) continue;

    const events = computeShotQualityEventsForRally({
      rally: r,
      rallyIndex,
      getTeamFromPlayerId: (playerId: number) =>
        playerId === 0 || playerId === 1 ? 0 : 1,
    });

    for (const ev of events) {
      if (
        filters.selectedShotNumber != null &&
        ev.shotNumber !== filters.selectedShotNumber
      ) {
        continue;
      }

      if (
        filters.selectedPlayerId != null &&
        ev.playerId !== filters.selectedPlayerId
      ) {
        continue;
      }

      if (
        filters.selectedShotType != null &&
        ev.normalizedShotType !== filters.selectedShotType.toLowerCase()
      ) {
        continue;
      }

      if (ev.qualityOverall == null) continue;
      if (ev.teamWonRally == null) continue;

      out.push({
        q: ev.qualityOverall,
        win: ev.teamWonRally ? 1 : 0,
      });
    }
  }

  return out;
}

function logisticRegression1D(
  xs: number[],
  ys: number[],
): { a: number; b: number } {
  let a = 0;
  let b = 0;

  const lr = 0.08;

  for (let step = 0; step < 400; step++) {
    let da = 0;
    let db = 0;

    for (let i = 0; i < xs.length; i++) {
      const z = a + b * xs[i];
      const p = 1 / (1 + Math.exp(-z));
      const err = p - ys[i];

      da += err;
      db += err * xs[i];
    }

    a -= (lr * da) / xs.length;
    b -= (lr * db) / xs.length;
  }

  return { a, b };
}

function buildImpactSummaryRows(args: {
  rows: KitchenArrivalRow[];
  filters: ShotQualityImpactFilters;
}): Array<{
  shotNumber: number;
  n: number;
  deltaPerPointOne: number | null;
}> {
  const { rows, filters } = args;

  const byShotNumber = new Map<number, QualityOutcomePair[]>();

  for (let rallyIndex = 0; rallyIndex < rows.length; rallyIndex++) {
    const r = rows[rallyIndex];
    if (!r) continue;

    const events = computeShotQualityEventsForRally({
      rally: r,
      rallyIndex,
      getTeamFromPlayerId: (playerId: number) =>
        playerId === 0 || playerId === 1 ? 0 : 1,
    });

    for (const ev of events) {
      if (
        filters.selectedPlayerId != null &&
        ev.playerId !== filters.selectedPlayerId
      ) {
        continue;
      }

      if (
        filters.selectedShotType != null &&
        ev.normalizedShotType !== filters.selectedShotType.toLowerCase()
      ) {
        continue;
      }

      if (ev.qualityOverall == null) continue;
      if (ev.teamWonRally == null) continue;

      const arr = byShotNumber.get(ev.shotNumber) ?? [];
      arr.push({
        q: ev.qualityOverall,
        win: ev.teamWonRally ? 1 : 0,
      });
      byShotNumber.set(ev.shotNumber, arr);
    }
  }

  const rowsOut: Array<{
    shotNumber: number;
    n: number;
    deltaPerPointOne: number | null;
  }> = [];

  for (const [shotNumber, pairs] of Array.from(byShotNumber.entries()).sort(
    (a, b) => a[0] - b[0],
  )) {
    const xs = pairs.map((p) => p.q);
    const ys = pairs.map((p) => p.win);

    if (xs.length < 6) {
      rowsOut.push({
        shotNumber,
        n: xs.length,
        deltaPerPointOne: null,
      });
      continue;
    }

    const { a, b } = logisticRegression1D(xs, ys);

    const predict = (q: number) => 1 / (1 + Math.exp(-(a + b * q)));
    const deltaPerPointOne = predict(0.6) - predict(0.5);

    rowsOut.push({
      shotNumber,
      n: xs.length,
      deltaPerPointOne,
    });
  }

  return rowsOut;
}

function impactStrengthLabel(delta: number | null): string {
  if (delta == null) return "too little data";

  const absPct = Math.abs(delta * 100);

  if (absPct < 1.5) return "weak";
  if (absPct < 4) return "modest";
  if (absPct < 8) return "strong";
  return "very strong";
}

function ImpactChart(props: {
  rows: KitchenArrivalRow[];
  filters: ShotQualityImpactFilters;
}) {
  const { rows, filters } = props;

  const [summarySort, setSummarySort] = React.useState<
    "shot_number" | "effect_size" | "impact_score" | "sample_size"
  >("shot_number");

  const summaryRows = React.useMemo(
    () => buildImpactSummaryRows({ rows, filters }),
    [rows, filters],
  );

  const sortedSummaryRows = React.useMemo(() => {
    const arr = [...summaryRows];

    arr.sort((a, b) => {
      if (summarySort === "shot_number") {
        return a.shotNumber - b.shotNumber;
      }

      if (summarySort === "sample_size") {
        return b.n - a.n;
      }

      if (summarySort === "effect_size") {
        return (
          Math.abs(b.deltaPerPointOne ?? Number.NEGATIVE_INFINITY) -
          Math.abs(a.deltaPerPointOne ?? Number.NEGATIVE_INFINITY)
        );
      }

      // impact_score
      const impactA = Math.abs((a.deltaPerPointOne ?? 0) * a.n);
      const impactB = Math.abs((b.deltaPerPointOne ?? 0) * b.n);
      return impactB - impactA;
    });

    return arr;
  }, [summaryRows, summarySort]);

  const pairs = React.useMemo(
    () => extractQualityOutcomePairs(rows, filters),
    [rows, filters],
  );

  const xs = pairs.map((p) => p.q);
  const ys = pairs.map((p) => p.win);

  const isAllShotNumbers = filters.selectedShotNumber == null;

  if (isAllShotNumbers) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            px: 1.2,
            pt: 0.9,
            pb: 0.7,
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            display: "flex",
            flexDirection: "column",
            gap: 0.8,
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 800 }}>
            Quality leverage by shot number
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {(
              [
                ["shot_number", "Shot number"],
                ["effect_size", "Effect size"],
                ["impact_score", "Impact score"],
                ["sample_size", "Sample size"],
              ] as const
            ).map(([value, label]) => {
              const selected = summarySort === value;

              return (
                <Button
                  key={value}
                  size="small"
                  variant="outlined"
                  onClick={() => setSummarySort(value)}
                  sx={{
                    minWidth: 0,
                    px: 0.85,
                    py: 0.32,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1,
                    textTransform: "none",
                    borderColor: "rgba(25, 118, 210, 0.55)",
                    color: "rgba(25, 118, 210, 0.92)",
                    boxShadow: "none",
                    backgroundColor: selected
                      ? "rgba(25, 118, 210, 0.14)"
                      : "transparent",
                    "&:hover": {
                      borderColor: "rgba(25, 118, 210, 0.8)",
                      backgroundColor: selected
                        ? "rgba(25, 118, 210, 0.18)"
                        : "rgba(25, 118, 210, 0.05)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {label}
                </Button>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ flex: "1 1 auto", minHeight: 0, overflow: "auto" }}>
          {sortedSummaryRows.length ? (
            <Box sx={{ px: 1.2, py: 0.8 }}>
              {sortedSummaryRows.map((row) => {
                const deltaPct =
                  row.deltaPerPointOne == null
                    ? null
                    : row.deltaPerPointOne * 100;

                const impactScore =
                  row.deltaPerPointOne == null
                    ? null
                    : Math.abs(row.deltaPerPointOne * row.n * 100);

                return (
                  <Box
                    key={row.shotNumber}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "90px 70px 160px 120px 1fr",
                      gap: 1.25,
                      alignItems: "center",
                      py: 0.7,
                      borderBottom: "1px solid rgba(0,0,0,0.06)",
                      fontSize: 13,
                    }}
                  >
                    <Box sx={{ fontWeight: 800 }}>Shot {row.shotNumber}</Box>

                    <Box sx={{ color: "rgba(0,0,0,0.72)" }}>n = {row.n}</Box>

                    <Box
                      sx={{
                        fontWeight: 700,
                        color:
                          row.deltaPerPointOne == null
                            ? "rgba(0,0,0,0.48)"
                            : "rgba(0,0,0,0.88)",
                      }}
                    >
                      {deltaPct == null
                        ? "Not enough data"
                        : `${deltaPct >= 0 ? "+" : ""}${deltaPct.toFixed(1)}% per +0.10`}
                    </Box>

                    <Box
                      sx={{
                        color:
                          impactScore == null
                            ? "rgba(0,0,0,0.45)"
                            : "rgba(0,0,0,0.72)",
                        fontWeight: impactScore == null ? 400 : 700,
                      }}
                    >
                      {impactScore == null
                        ? "—"
                        : `impact ${impactScore.toFixed(1)}`}
                    </Box>

                    <Box
                      sx={{
                        color:
                          row.deltaPerPointOne == null
                            ? "rgba(0,0,0,0.45)"
                            : "rgba(0,0,0,0.68)",
                        fontStyle:
                          row.deltaPerPointOne == null ? "italic" : "normal",
                      }}
                    >
                      {impactStrengthLabel(row.deltaPerPointOne)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Box sx={{ p: 2, fontSize: 13 }}>No matching shots.</Box>
          )}
        </Box>
      </Box>
    );
  }

  if (xs.length < 6) {
    return <Box sx={{ p: 2, fontSize: 13 }}>Not enough data.</Box>;
  }

  const { a, b } = logisticRegression1D(xs, ys);

  function predict(q: number): number {
    return 1 / (1 + Math.exp(-(a + b * q)));
  }

  const curve: { q: number; p: number }[] = [];
  for (let q = 0; q <= 1.0001; q += 0.02) {
    curve.push({
      q,
      p: predict(q),
    });
  }

  const midDelta = predict(0.6) - predict(0.5);
  const deltaPct = midDelta * 100;
  const impactScore = Math.abs(midDelta * pairs.length * 100);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          px: 1,
          pt: 0.5,
          pb: 1,
          fontSize: 13,
          fontWeight: 700,
          display: "flex",
          flexWrap: "wrap",
          gap: 1.25,
        }}
      >
        <Box>n = {pairs.length}</Box>
        <Box>
          Δ win prob per +0.10 quality near mid = {deltaPct >= 0 ? "+" : ""}
          {deltaPct.toFixed(1)}%
        </Box>
        <Box sx={{ color: "rgba(0,0,0,0.72)" }}>
          impact {impactScore.toFixed(1)}
        </Box>
        <Box sx={{ color: "rgba(0,0,0,0.68)" }}>
          {impactStrengthLabel(midDelta)}
        </Box>
      </Box>

      <Box sx={{ flex: "1 1 auto", minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={curve}>
            <XAxis
              dataKey="q"
              type="number"
              domain={[0, 1]}
              tickFormatter={(v: number) => v.toFixed(1)}
            />
            <YAxis
              domain={[0, 1]}
              tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
            />
            <Line
              type="monotone"
              dataKey="p"
              dot={false}
              strokeWidth={3}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

type ShotQualityImpactFilters = {
  selectedPlayerId: number | null;
  selectedShotType: string | null;
  selectedShotNumber: number | null;
};

type QualityOutcomePair = {
  q: number;
  win: 0 | 1;
};
